import { useEffect, useState } from 'react'
import { Card, CardColors, CardVariants, HeroCard, Link, Text } from '@/ui'
import { ImageBackgroundColors, TextColors, TypographyStyles } from '@/styles'

import { useAppDispatch } from '@/utils/typeHelpers'
import { fetchAllocationGuidesIfNeeded } from '@/src/actions/allocationGuideActions'
import { fetchAllocationRulesIfNeeded } from '@/src/actions/allocationRuleActions'
import { fetchFinancialAccountsIfNeeded } from '@/src/actions/financialAccountActions'

import { AllocationGuidesTable } from './AllocationGuidesTable'
import { AllocationRulesCard } from './AllocationRulesCard'
import { AllocationGuideSummary } from './AllocationGuideSummary'
import { CreateAllocationCard } from './CreateAllocationCard'
import { CreateAllocationGuideCard } from './CreateAllocationGuideCard'
import {
  getAllocationGuides,
  getAllocationRules,
} from '../../selectors/allocationGuideSelectors'
import { useReselector, useToggle } from '@/utils/sharedHooks'
import { selectFinancialAccounts } from '@/src/selectors/financeSelectors'
import { useAnalyticsView, useAnalyticsTrack } from '@/features/amplitude'
import { PageHeader } from '@/components/PageHeader'

// TODO: Migrate to V2 skipcq: JS-0099
import { Loader } from 'semantic-ui-react'

export const AllocationGuidePanel = () => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()

  const allocationGuides = useReselector(getAllocationGuides)
  const allocationRules = useReselector(getAllocationRules)
  const financialAccounts = useReselector(selectFinancialAccounts)
  const [displayBanner, toggleBanner] = useToggle(true)

  const noRulesSet = Object.keys(allocationRules).length === 0
  const noFinancialAccounts = Object.keys(financialAccounts).length === 0

  const [loading, setLoading] = useState(true)

  const pageView = useAnalyticsView()

  useEffect(() => {
    pageView('allocation guide')
  }, [pageView])

  useEffect(() => {
    const fetch = async () => {
      await dispatch(fetchAllocationGuidesIfNeeded())
      await dispatch(fetchAllocationRulesIfNeeded())
      await dispatch(fetchFinancialAccountsIfNeeded())
      setLoading(false)
    }
    fetch()
  }, [dispatch])

  const closeBanner = () => {
    toggleBanner()
    track('closed allocation guide banner')
  }
  const renderRightPanel = () => {
    // This means that the user has not set up any allocation rules
    if (loading) {
      return <Loader inline active />
    }

    /* If user has no financial accounts, we encourage them to set it up*/
    if (noFinancialAccounts) {
      return null
    } else if (noRulesSet) {
      return <CreateAllocationCard />
    } else {
      return (
        <>
          <AllocationGuideSummary />
          <CreateAllocationGuideCard />
        </>
      )
    }
  }

  /*
    If a user has no financial accounts, we encourage them to:
    1. set it up
    2. If they cannot set it up and their account is managed outside of Heard, to message the bookkeeper to help them complete
  */
  const renderAllocationBody = () => {
    if (noFinancialAccounts) {
      return (
        <Card color={CardColors.LEVEL_0}>
          <span className="flex flex-col gap-4">
            <Text variant={TypographyStyles.H3}>
              It looks there are no financial accounts connected to Heard.
            </Text>
            <Text variant={TypographyStyles.BODY_SM}>
              Without connected accounts or insight into your business profits,
              we are unable to provide you with an allocation guide at this
              time.
            </Text>
            <Text variant={TypographyStyles.BODY_SM}>
              To get set up, head over to your{' '}
              <Link href={'/accounts#connected-institutions'} newPage={false}>
                Accounts page
              </Link>{' '}
              and connect your business bank account(s) and/or business credit
              card(s).
            </Text>
            <Text variant={TypographyStyles.BODY_SM}>
              What if my bank accounts can&#39;t connect to Heard, and my
              accountant suggested an alternate solution?
            </Text>
            <Text variant={TypographyStyles.BODY_SM}>
              <Link href="/conversations" variant="secondary">
                Message the Heard Team
              </Link>{' '}
              and they will get you set up with your Allocation Guide!
            </Text>
          </span>
        </Card>
      )
    }

    return (
      <>
        {!noRulesSet && <AllocationRulesCard />}

        <Card color={CardColors.LEVEL_0}>
          <Text variant={TypographyStyles.H2} className="my-4">
            Past Allocations:
          </Text>
          <AllocationGuidesTable allocationGuides={allocationGuides} />
        </Card>
      </>
    )
  }

  return (
    <div id="allocationGuidePanel">
      <PageHeader header="Allocation Guides" className="mb-4" />
      <div className="grid grid-cols-1 gap-6">
        {/* Banner Row */}
        {displayBanner && (
          <HeroCard
            variant={CardVariants.LEFT_ALIGNED}
            title="What is an Allocation Guide?"
            description="Your Allocation Guide helps calculate how much money you should
            set aside in order to stay on track with your saving goals. Let
            us know how you'd like to distribute your business profit,
            and we'll provide you with estimates each month on how much
            to allocate based on your profit."
            onClose={() => closeBanner()}
            imageAlt="Allocation Guide"
            imageWidth={200}
            imageColor={ImageBackgroundColors.YELLOW}
            imageSrc="https://heard-images.s3.us-west-1.amazonaws.com/assets/piggy_bank_allocation.svg"
          >
            <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
              Note: Unfortunately, Heard can’t move your money for you. You will
              need to transfer your money manually.
            </Text>
          </HeroCard>
        )}

        {/* Create Allocation Card Row */}
        {noRulesSet && <CreateAllocationCard />}

        {/* Allocation + Right Panel Row */}
        {!noRulesSet && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7">{renderAllocationBody()}</div>
            <div className="lg:col-span-5">{renderRightPanel()}</div>
          </div>
        )}
      </div>
    </div>
  )
}
